import React, { useCallback, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, IconButton, Tooltip } from '@mui/material';

import { useAbortReportingLeDataSession } from '~/hooks/apis/reporting/useAbortReportingLeDataSession';
import { useRecentReportingLeDataSessions } from '~/hooks/apis/reporting/useRecentReportingLeDataSessions';
import { useReplyPageBridge } from '~/hooks/apis/reporting/useReplyPageBridge';

import LensChatHistory from '../LensChatHistory';
import UiStreamChat from '../uiStream/UiStreamChat';

import type { PageBridge } from './pageBridge';

export default function PageChat({
  bridge,
  width,
  onClose,
}: {
  bridge: PageBridge;
  width: string;
  onClose: () => void;
}) {
  const sessionKey = `askrichard.page.${bridge.pageId}`;
  const [sessionId, setSessionId] = useState<string | null>(() => sessionStorage.getItem(sessionKey));
  const [generation, setGeneration] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const streaming = useRef(false);
  const activeGeneration = useRef(generation);
  activeGeneration.current = generation;
  const { mutateAsync: abortSession } = useAbortReportingLeDataSession();
  const sessions = useRecentReportingLeDataSessions(true);
  const rememberSession = useCallback(
    (id: string | null) => {
      if (!id || activeGeneration.current !== generation) return;
      setSessionId(id);
      sessionStorage.setItem(sessionKey, id);
    },
    [generation, sessionKey],
  );
  const switchSession = (next: string | null) => {
    if (streaming.current && sessionId && sessionId !== next) void abortSession(sessionId).catch(() => undefined);
    activeGeneration.current = generation + 1;
    if (next) sessionStorage.setItem(sessionKey, next);
    else sessionStorage.removeItem(sessionKey);
    setSessionId(next);
    setHistoryOpen(false);
    setGeneration((value) => value + 1);
  };
  const startNewChat = () => switchSession(null);
  const { mutateAsync: reply } = useReplyPageBridge();
  const actions = (
    <>
      <Tooltip title="History">
        <IconButton
          aria-label="History"
          aria-expanded={historyOpen}
          onClick={() => setHistoryOpen((value) => !value)}
          size="small"
        >
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="New chat">
        <IconButton aria-label="New chat" onClick={startNewChat} size="small">
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
  return (
    <Box
      component="aside"
      aria-label="askRichard page chat"
      sx={{
        position: 'fixed',
        right: 0,
        top: 'var(--page-chat-top, 64px)',
        bottom: 0,
        width: { xs: '100%', lg: width },
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Tooltip title="Hide chat" placement="right">
        <IconButton
          aria-label="Collapse chat"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1102,
            color: 'text.secondary',
            opacity: 0.5,
            '&:hover, &:focus-visible': { opacity: 1 },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <UiStreamChat
        key={generation}
        variant="pane"
        controlledSessionId={sessionId}
        onSessionChange={rememberSession}
        onStreamingChange={(value) => {
          streaming.current = value;
        }}
        pageBridge={{ ...bridge, reply }}
        toolbarActions={actions}
        toolbarLeadingAction={
          <Tooltip title="Hide chat" placement="right">
            <IconButton aria-label="Hide chat" onClick={onClose} size="small">
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        historyPanel={
          historyOpen ? (
            <LensChatHistory
              title="Conversations"
              emptyText="No previous conversations yet."
              sessions={sessions.data ?? []}
              isLoading={sessions.isLoading}
              selectedSessionId={sessionId}
              onNewChat={startNewChat}
              onOpenSession={switchSession}
            />
          ) : undefined
        }
        composerPlaceholder={`Ask about ${bridge.title}…`}
        subjectChip={bridge.title}
      />
    </Box>
  );
}
